import React, { useState, useEffect } from 'react';
import './PrescriptionsList.css';

/**
 * PrescriptionsList - Display all prescriptions in list format
 * Shows: Doctor, Clinic, Medicine count, Status, Date
 * Click to view full details
 */

export default function PrescriptionsList() {
  const [prescriptions, setPrescriptions] = useState([]);
  const [selectedRx, setSelectedRx] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPrescriptions();
  }, []);

  const fetchPrescriptions = async () => {
    try {
      const response = await fetch('/api/patient/prescriptions/', {
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        setPrescriptions(data.prescriptions || []);
      } else {
        setError('Failed to load prescriptions');
      }
    } catch (err) {
      setError('Could not fetch prescriptions');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="prescriptions-loading">
        <div className="spinner"></div>
        <p>Loading prescriptions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="prescriptions-error">
        <p>❌ {error}</p>
        <button onClick={fetchPrescriptions}>Retry</button>
      </div>
    );
  }

  if (prescriptions.length === 0) {
    return (
      <div className="prescriptions-empty">
        <div className="empty-icon">💊</div>
        <p>No prescriptions yet</p>
        <small>Prescriptions from your doctor will appear here</small>
      </div>
    );
  }

  // If viewing details
  if (selectedRx) {
    return (
      <div className="prescription-detail">
        <button className="back-btn" onClick={() => setSelectedRx(null)}>
          ← Back
        </button>

        <div className="detail-header">
          <h2>Prescription Details</h2>
          <span className={`status-badge status-${selectedRx.status}`}>
            {selectedRx.status?.toUpperCase() || 'ACTIVE'}
          </span>
        </div>

        <div className="detail-card">
          <div className="detail-row">
            <label>Doctor</label>
            <span>{selectedRx.doctor_name || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <label>Clinic</label>
            <span>{selectedRx.clinic_name || 'N/A'}</span>
          </div>
          <div className="detail-row">
            <label>Issued Date</label>
            <span>
              {selectedRx.created_at
                ? new Date(selectedRx.created_at).toLocaleDateString()
                : 'N/A'}
            </span>
          </div>
          <div className="detail-row">
            <label>Notes</label>
            <span>{selectedRx.notes || '-'}</span>
          </div>
        </div>

        <div className="medicines-section">
          <h3>Medicines ({selectedRx.medicines?.length || 0})</h3>
          <div className="medicines-list">
            {selectedRx.medicines?.map((med, idx) => (
              <div key={idx} className="medicine-card">
                <div className="medicine-name">{med.medicine_name}</div>
                <div className="medicine-details">
                  <div>
                    <strong>Dosage:</strong> {med.dosage}
                  </div>
                  <div>
                    <strong>Frequency:</strong> {med.frequency}
                  </div>
                  <div>
                    <strong>Duration:</strong> {med.duration_days} days
                  </div>
                  <div>
                    <strong>Quantity:</strong> {med.quantity}
                  </div>
                  <div>
                    <strong>Instructions:</strong> {med.instructions}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  // List view
  return (
    <div className="prescriptions-list-container">
      <div className="prescriptions-list">
        {prescriptions.map((rx) => (
          <div
            key={rx.id}
            className="prescription-card"
            onClick={() => setSelectedRx(rx)}
          >
            <div className="card-header">
              <h3>{rx.doctor_name || 'Doctor'}</h3>
              <span className={`status-badge status-${rx.status}`}>
                {rx.status?.toUpperCase() || 'ACTIVE'}
              </span>
            </div>

            <div className="card-info">
              <div className="info-item">
                <span className="label">Clinic</span>
                <span className="value">{rx.clinic_name || 'N/A'}</span>
              </div>
              <div className="info-item">
                <span className="label">Medicines</span>
                <span className="value badge">
                  {rx.medicines?.length || 0}
                </span>
              </div>
              <div className="info-item">
                <span className="label">Issued</span>
                <span className="value">
                  {rx.created_at
                    ? new Date(rx.created_at).toLocaleDateString()
                    : 'N/A'}
                </span>
              </div>
            </div>

            <div className="card-footer">
              <button className="view-btn">View Details →</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
